import { useRef, useEffect, useState } from 'react';

import logoHorizontal from '../assets/images/logo-horizontal.svg';
import logoVertical from '../assets/images/logo-vertical.svg';
import iconAI from '../assets/icons/artificial-intelligence-icon.svg';
import iconDocumentation from '../assets/icons/documents-icon.svg';
import iconCustomization from '../assets/icons/customization-icon.svg';
import iconIntegration from '../assets/icons/integration-icon.svg';
import animatedImageMid from '../assets/images/animated-image-mid.png';
import animatedImageLeft from '../assets/images/animated-image-left.png';
import animatedImageRight from '../assets/images/animated-image-right.png';
import animationInvoice from '../assets/images/animation.png';
import { Link } from 'react-router-dom';
import { API_BASE_URL, POST_SUBMIT_EMAIL_FORM } from '../common/constants/constants';
import axios from 'axios';
import { MessageModal } from '../components/other/MessageModal';

const Landing = () => {
  const animationOneElementRefs = useRef<Array<HTMLDivElement | null>>([]);
  const animationTwoElementRefs = useRef<Array<HTMLElement | null>>([]);
  const animationThreeElementRefs = useRef<Array<HTMLElement | null>>([]);

  const [name, setName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [company, setCompany] = useState<string>('');
  const [message, setMessage] = useState<string>('');

  const [buttonDisabled, setButtonDisabled] = useState<boolean>(false);

  const [modalOn, setModalOn] = useState<boolean>(false);
  const [modalFirstText, setModalFirstText] = useState<string>('');
  const [modalSecondText, setModalSecondText] = useState<string>('');

  const emailFormRef = useRef<HTMLDivElement>(null);

  const focusEmailForm = () => {
    emailFormRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleScroll = () => {
    animationOneElementRefs.current.forEach((element) => {
      if (element) {
        const { top, bottom } = element.getBoundingClientRect();

        if (top < window.innerHeight && bottom > 0) {
          element.classList.add('animateOne');
        } else {
          element.classList.remove('animateOne');
        }
      }
    });
    animationTwoElementRefs.current.forEach((element) => {
      if (element) {
        const { top, bottom } = element.getBoundingClientRect();

        if (top < window.innerHeight && bottom > 0) {
          element.classList.add('animateTwo');
        } else {
          element.classList.remove('animateTwo');
        }
      }
    });
    animationThreeElementRefs.current.forEach((element) => {
      if (element) {
        const { top, bottom } = element.getBoundingClientRect();

        if (top < window.innerHeight && bottom > 0) {
          element.classList.add('animateThree');
        } else {
          element.classList.remove('animateThree');
        }
      }
    });
  };

  useEffect(() => {
    handleScroll();

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const onFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const emailIsValid = email.includes('@') && email.length > 5;

    if (emailIsValid) {
      setButtonDisabled(true);
      await axios
        .create({
          baseURL: API_BASE_URL,
        })
        .post(POST_SUBMIT_EMAIL_FORM, {
          name,
          email,
          company,
          message,
        });
      setButtonDisabled(false);

      setName('');
      setEmail('');
      setCompany('');
      setMessage('');

      setModalFirstText('Your message has been sent.');
      setModalSecondText("We'll get back to you as soon as possible.");
    } else {
      setModalFirstText('Your email is in wrong format.');
      setModalSecondText('');
    }
    setModalOn(true);
  };

  return (
    <>
      {modalOn && <MessageModal setModalOn={setModalOn} firstText={modalFirstText} secondText={modalSecondText} />}
      <div className='landing-page'>
        <div className='landing-page__header'>
          <div className='flex-center relative'>
            <picture className='landing-page__header__horizontal-logo'>
              <img className='landing-page__header__horizontal-logo__img' src={logoHorizontal} alt='logo-horizontal' />
            </picture>
            <div className='beta-version color-white font-light font-italic'>
              <span>Beta</span>
            </div>
          </div>
          <div className='landing-page_header__buttons'>
            <Link to='/login' className='button-secondary margin-right-2'>
              Login
            </Link>
            <button className='button-primary' onClick={focusEmailForm}>
              Get in touch
            </button>
          </div>
        </div>
        <div className='full-width-line-dark-300'></div>
        <div className='landing-page__hero'>
          <picture className='landing-page__hero__vertical-logo'>
            <img className='landing-page__hero__vertical-logo__img' src={logoVertical} alt='logo-vertical' />
          </picture>
          <div className='landing-page__hero__floating-article padding-top-2'>
            <p className='landing-page__hero__floating-article__paragraph font-h3-regular dark-200'>AI-based software for fast document processing</p>
            <button onClick={focusEmailForm} className='button-primary margin-bottom-2 font-h5-medium'>
              Request Demo
            </button>
          </div>
        </div>
        <div className='landing-page__animated-images'>
          <picture className='landing-page__animated-images__animated-image-mid'>
            <img className='landing-page__animated-images__animated-image-mid__img' src={animatedImageMid} alt='animatedImageMid' />
          </picture>
          <div className='landing-page__animated-images__side-images'>
            <picture className='landing-page__animated-images__animated-image-side'>
              <img className='landing-page__animated-images__animated-image-side__img' src={animatedImageLeft} alt='animatedImageLeft' />
            </picture>
            <picture className='landing-page__animated-images__animated-image-side'>
              <img className='landing-page__animated-images__animated-image-side__img' src={animatedImageRight} alt='animatedImageRight' />
            </picture>
          </div>
        </div>
        <section className='landing-page__section'>
          <div className='landing-page__section__box'>
            <h2 className='font-h1-regular margin-bottom-2'>Process any document using AI + OCR</h2>
            <div>
              <p className='font-h3-regular dark-200'>
                SimplyDoc uses the power of
                <span className='rectangle-4024'>
                  <span className='shape-3'>&nbsp;</span>
                  <span className='shape-1'>&nbsp;</span>
                  <span className='rectangle-4024__text font-light'>Optical Character Recognition</span>
                  <span className='shape-1'>&nbsp;</span>
                  <span className='shape-2'>&nbsp;</span>
                </span>
                and
                <span className='rectangle-4025'>
                  <span className='shape-3'>&nbsp;</span>
                  <span className='shape-1'>&nbsp;</span>
                  <span className='rectangle-4025__text font-light'>Machine Learning</span>
                  <span className='shape-1'>&nbsp;</span>
                  <span className='shape-2'>&nbsp;</span>
                </span>
                to extract data from your document
              </p>
            </div>
          </div>
        </section>
        <div className='features-container'>
          <div className='landing-page__features container padding-left-2 padding-right-2 padding-top-5 padding-bottom-5'>
            <h2 className='font-h1-regular text-center'>What are main features of software that uses AI to read documents and extract data?</h2>
            <div className='row'>
              <article ref={(el) => (animationTwoElementRefs.current[0] = el)} className='landing-page__features__article col-md-6'>
                <picture className='landing-page__features__icon-5'>
                  <img className='landing-page__features__icon-5__img' src={iconAI} alt='' />
                </picture>
                <h3 className='font-h3-regular margin-bottom-1'>Machine learning or deep learning algorithms</h3>
                <p className='font-h5-regular dark-300 margin-top-0'>
                  These algorithms allow the software to learn from examples and improve its performance over time.
                </p>
              </article>
              <article ref={(el) => (animationThreeElementRefs.current[0] = el)} className='landing-page__features__article col-md-6'>
                <picture className='landing-page__features__icon-5'>
                  <img className='landing-page__features__icon-5__img' src={iconDocumentation} alt='' />
                </picture>
                <h3 className='font-h3-regular margin-bottom-1'>Support for multiple document types</h3>
                <p className='font-h5-regular dark-300 margin-top-0'>
                  The software may be able to handle different types of documents, such as PDFs, Word documents, or scanned images.
                </p>
              </article>
              <article ref={(el) => (animationTwoElementRefs.current[1] = el)} className='landing-page__features__article col-md-6'>
                <picture className='landing-page__features__icon-5'>
                  <img className='landing-page__features__icon-5__img' src={iconCustomization} alt='' />
                </picture>
                <h3 className='font-h3-regular margin-bottom-1'>Customization and configurability</h3>
                <p className='font-h5-regular dark-300 margin-top-0'>
                  The software may allow users to specify which data to extract and how to organize it, as well as to train the algorithms on specific
                  types of documents.
                </p>
              </article>
              <article ref={(el) => (animationThreeElementRefs.current[1] = el)} className='landing-page__features__article col-md-6'>
                <picture className='landing-page__features__icon-5'>
                  <img className='landing-page__features__icon-5__img' src={iconIntegration} alt='' />
                </picture>
                <h3 className='font-h3-regular margin-bottom-1'>Integration with other systems</h3>
                <p className='font-h5-regular dark-300 margin-top-0'>
                  The extracted data can be easily integrated with other systems and used for various purposes, such as to populate a database,
                  generate reports, or provide input for other AI applications.
                </p>
              </article>
            </div>
          </div>
        </div>
        <section className='landing-page__section-one'>
          <div className='landing-page__section-one__cta text-center'>
            <h2 className='font-h1-regular font-light color-white margin-bottom-2'>Interested in saving time and money?</h2>
            <button onClick={focusEmailForm} className='button-primary font-h5-medium'>
              Get in touch
            </button>
          </div>
          <div className='landing-page__section-one__easy-to-use'>
            <h2 className='font-h1-regular text-center margin-bottom-2'>How to get data from documents, with help from AI</h2>
            <div className='landing-page__section-one__easy-to-use__list'>
              <div className='landing-page__section-one__easy-to-use__list__number'>
                <span>1</span>
              </div>
              <div className='landing-page__section-one__easy-to-use__list__text'>
                <h3 className='font-h3-regular margin-bottom-1'>Input</h3>
                <p className='font-h5-regular dark-300 margin-bottom-0 font-h4-regular'>
                  Software receives the document to be processed. The document can be in various formats such as a PDF, Word document, or scanned
                  image.
                </p>
              </div>
            </div>
            <div className='landing-page__section-one__easy-to-use__list md-margin-left-6perc'>
              <div className='landing-page__section-one__easy-to-use__list__number'>
                <span>2</span>
              </div>
              <div className='landing-page__section-one__easy-to-use__list__text'>
                <h3 className='font-h3-regular margin-bottom-1'>Preprocessing</h3>
                <p className='font-h5-regular dark-300 margin-bottom-0 font-h4-regular'>
                  The document is preprocessed to extract the text and clean up any formatting or other irregularities.
                </p>
              </div>
            </div>
            <div className='landing-page__section-one__easy-to-use__list md-margin-left-12perc'>
              <div className='landing-page__section-one__easy-to-use__list__number'>
                <span>3</span>
              </div>
              <div className='landing-page__section-one__easy-to-use__list__text'>
                <h3 className='font-h3-regular margin-bottom-1'>Data extraction</h3>
                <p className='font-h5-regular dark-300 margin-bottom-0 font-h4-regular'>
                  The software uses machine learning or deep learning algorithms to identify and extract the specific pieces of data that are of
                  interest, such as product names, prices, or dates
                </p>
              </div>
            </div>
            <div className='landing-page__section-one__easy-to-use__list md-margin-left-18perc'>
              <div className='landing-page__section-one__easy-to-use__list__number'>
                <span>4</span>
              </div>
              <div className='landing-page__section-one__easy-to-use__list__text'>
                <h3 className='font-h3-regular margin-bottom-1'>Structuring</h3>
                <p className='font-h5-regular dark-300 margin-bottom-0 font-h4-regular'>
                  The extracted data is organized into a structured format, such as a table or database, to make it easier to use and analyze.
                </p>
              </div>
            </div>
            <div className='landing-page__section-one__easy-to-use__list md-margin-left-24perc'>
              <div className='landing-page__section-one__easy-to-use__list__number'>
                <span>5</span>
              </div>
              <div className='landing-page__section-one__easy-to-use__list__text'>
                <h3 className='font-h3-regular margin-bottom-1'>Output</h3>
                <p className='font-h5-regular dark-300 margin-bottom-0 font-h4-regular'>
                  The structured data is outputted in a format that can be easily consumed by other systems or applications.
                </p>
              </div>
            </div>
            <p className='landing-page__section-one__easy-to-use__paragraph font-h5-regular dark-400 padding-bottom-2'>
              Note that the specific steps in the process may vary depending on the specific software and the details of the data extraction task. The
              steps above provide a general overview of the process, but the actual implementation may differ depending on the requirements of the
              software and the data to be extracted.
            </p>
          </div>
        </section>

        <section className='landing-page__section-two padding-top-3 padding-bottom-2'>
          <div className='landing-page-section-two-header text-center margin-bottom-2'>
            <h2 className='font-h1-regular margin-bottom-1'>What are the benefits of automated document processing?</h2>
            <p className='font-h5-regular dark-200'>
              Automated document processing refers to the use of software to automatically extract data and information from documents, such as pdfs,
              word documents, or scanned images. The benefits of automated document processing include the following:
            </p>
          </div>
          <div className='landing-page__section-two__benefits'>
            <div ref={(el) => (animationOneElementRefs.current[0] = el)} className='landing-page__section-two__benefits__benefit'>
              <h4 className='font-18-medium primary-200 landing-page__section-two__benefits__benefit__title'>Increased efficiency</h4>
              <p className='font-h5-regular dark-400 landing-page__section-two__benefits__benefit__paragraph'>
                Save time and effort by automatically extracting data from documents, which would otherwise need to be done manually. This can improve
                the speed and accuracy of data entry, as well as free up staff to focus on other tasks.
              </p>
            </div>
            <div ref={(el) => (animationOneElementRefs.current[1] = el)} className='landing-page__section-two__benefits__benefit'>
              <h4 className='font-18-medium primary-200 landing-page__section-two__benefits__benefit__title'>Improved accuracy</h4>
              <p className='font-h5-regular dark-400 landing-page__section-two__benefits__benefit__paragraph'>
                Reduce costs by eliminating the need for manual data entry. This can save money on labor costs, as well as reduce the time and
                resources required to process large volumes of documents.
              </p>
            </div>
            <div ref={(el) => (animationOneElementRefs.current[2] = el)} className='landing-page__section-two__benefits__benefit'>
              <h4 className='font-18-medium primary-200 landing-page__section-two__benefits__benefit__title'>Reduced costs</h4>
              <p className='font-h5-regular dark-400 landing-page__section-two__benefits__benefit__paragraph'>
                Avoid errors and improve the accuracy of the extracted data. By using machine learning algorithms, the software can learn from
                examples and improve its performance over time. This can result in more accurate and reliable data compared to manual data entry.
              </p>
            </div>
            <div ref={(el) => (animationOneElementRefs.current[3] = el)} className='landing-page__section-two__benefits__benefit'>
              <h4 className='font-18-medium primary-200 landing-page__section-two__benefits__benefit__title'>Enhanced data analysis</h4>
              <p className='font-h5-regular dark-400 landing-page__section-two__benefits__benefit__paragraph'>
                Providing structured data that can be easily analyzed and used for various purposes. This can enable businesses to gain insights from
                their data and make better-informed decisions.
              </p>
            </div>
            <div ref={(el) => (animationOneElementRefs.current[4] = el)} className='landing-page__section-two__benefits__benefit'>
              <h4 className='font-18-medium primary-200 landing-page__section-two__benefits__benefit__title'>Improved compliance</h4>
              <p className='font-h5-regular dark-400 landing-page__section-two__benefits__benefit__paragraph'>
                Ensure that businesses comply with regulations that require accurate and timely data entry. This can reduce the risk of fines and
                other penalties for non-compliance.
              </p>
            </div>
            {/* <div ref={elementRef}>My animated element</div> */}
          </div>
          <picture className='landing-page__section-two__animation-invoice'>
            <img className='landing-page__section-two__animation-invoice__img' src={animationInvoice} alt='animation-invoice' />
          </picture>
        </section>
        <section className='landing-page__section-three' ref={emailFormRef}>
          <div className='landing-page__section-three-box'>
            <h2 className='landing-page__section-three__text font-h1-regular font-light text-center color-white'>
              We are here to help you grow your business
            </h2>

            <form className='landing-page__section-three__inputs' onSubmit={onFormSubmit}>
              <div className='landing-page__section-three__inputs__input-field margin-bottom-2'>
                <label className='font-h6-regular color-white padding-left-1 font-light' htmlFor=''>
                  Your Name
                </label>
                <input
                  value={name}
                  onChange={(event) => setName(event?.target.value)}
                  className='landing-page__section-three__inputs__input-field__input'
                  type='text'
                  required
                />
              </div>
              <div className='landing-page__section-three__inputs__input-field margin-bottom-2'>
                <label className='font-h6-regular color-white padding-left-1 font-light' htmlFor=''>
                  Email address
                </label>
                <input
                  value={email}
                  onChange={(event) => setEmail(event?.target.value)}
                  className='landing-page__section-three__inputs__input-field__input'
                  type='text'
                  required
                />
              </div>
              <div className='landing-page__section-three__inputs__input-field margin-bottom-2'>
                <label className='font-h6-regular color-white padding-left-1 font-light' htmlFor=''>
                  Company (optional)
                </label>
                <input
                  value={company}
                  onChange={(event) => setCompany(event?.target.value)}
                  className='landing-page__section-three__inputs__input-field__input'
                  type='text'
                />
              </div>
              <div className='landing-page__section-three__inputs__input-field margin-bottom-2'>
                <label className='font-h6-regular color-white padding-left-1 font-light' htmlFor=''>
                  Your message
                </label>
                <textarea
                  value={message}
                  onChange={(event) => setMessage(event?.target.value)}
                  className='landing-page__section-three__inputs__input-field__textarea'
                  required
                />
              </div>
              <div className='flex-align-center'>
                <button disabled={buttonDisabled} className='button-primary font-h5-medium'>
                  Send message
                </button>
              </div>
            </form>
            <div className=' landing-page__section-three__footer font-h6-regular font-light color-white text-center padding-top-2 margin-top-3 border-top-dark-400'>
              ©2024 SimplyDoc | All rights reserved
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Landing;
